import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import React, { memo, useMemo } from 'react'
import { Controller, Resolver, SubmitHandler, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as yup from 'yup'

import { RootState } from '../../../partner/redux/store'
import convertBigIntToHex from '../../../lib/convertBigIntToHex'
import convertHexToBigInt, { reverseHex } from '../../../lib/convertHexToBigInt'
import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import { MAX_COMMENT_LENGTH, MAX_EXTERNAL_ID_LENGTH } from '../../constants/InputsLength'
import { useCompanyQuery } from '../../hooks/useFetchEndpoints'
import useText from '../../hooks/useText'
import { isFetchBaseQueryError } from '../../redux/api/fetchBaseQueryWithReauth'
import { NfcTag, useNfcTagCreateMutation, useNfcTagUpdateMutation } from '../../redux/api/web-api'
import { pad } from '../../services/utilsService'
import { Dialog } from '../../uikit/Dialog'
import { Dropdown } from '../../uikit/Dropdown'
import { Input } from '../../uikit/Input'
import { useToast } from '../../uikit/ToastProvider'

const formId = 'rfid-tag-form'
const hexadecimalPattern = /^[0-9A-Fa-f]+$/
const decimalPattern = /^[0-9]+$/

const padHexId = (hexId: string) => {
    if (hexId.length <= 8) return hexId.padStart(8, '0')
    if (hexId.length <= 14) return hexId.padStart(14, '0')
    if (hexId.length <= 20) return hexId.padStart(20, '0')
    return hexId
}

export type RfidTagDialogProps = {
    rfidTag?: NfcTag
    targetCompanyId?: string
    isNew?: boolean
    visible: boolean
    onHide: () => void
}

type RfidTagFormValues = {
    isReversed: boolean
    hexId: string
    decId: string
    externalId: string
    comment: string
    companyId: string
}

const getFormValues = (
    rfidTag: NfcTag | undefined,
    selectedCompanyId: string,
    targetCompanyId?: string
): RfidTagFormValues => ({
    isReversed: false,
    hexId: rfidTag?.id ?? '',
    decId: rfidTag?.id ? convertHexToBigInt(rfidTag.id) : '',
    comment: rfidTag?.comment ?? '',
    externalId: rfidTag?.externalId ?? '',
    companyId: isAdminDomain ? selectedCompanyId : rfidTag?.companyId ?? targetCompanyId ?? '',
})

export const RfidTagDialog = memo(function RfidTagDialog({
    rfidTag,
    targetCompanyId,
    isNew = false,
    visible,
    onHide,
}: RfidTagDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const { partnerId, selectedCompanyId } = useSelector((state: RootState) => state.user)
    const { data: organisationsData } = useCompanyQuery(partnerId)
    const [createRfidTag] = useNfcTagCreateMutation()
    const [updateRfidTag] = useNfcTagUpdateMutation()
    const isAddingNewTag = isNew || !rfidTag
    const organisations = organisationsData ?? []
    const initialValues = useMemo(
        () => getFormValues(rfidTag, selectedCompanyId, targetCompanyId),
        [rfidTag, selectedCompanyId, targetCompanyId]
    )

    const schema = yup.object().shape({
        hexId: yup
            .string()
            .nullable()
            .max(20, text('msgHexToLong'))
            .matches(hexadecimalPattern, text('msgInvalidHexadecimal')),
        decId: yup
            .number()
            .integer()
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .moreThan(0, text('msgInvalidDecimal'))
            .typeError(text('msgInvalidDecimal'))
            .required(text('msgDecimalRequired')),
        externalId: yup
            .string()
            .nullable()
            .max(MAX_EXTERNAL_ID_LENGTH, text('msgLengthLimit', { maxLength: MAX_EXTERNAL_ID_LENGTH })),
        comment: yup
            .string()
            .nullable()
            .max(MAX_COMMENT_LENGTH, text('msgLengthLimit', { maxLength: MAX_COMMENT_LENGTH })),
    })

    const {
        control,
        getValues,
        handleSubmit,
        register,
        reset,
        setValue,
        formState: { errors, isSubmitted, isSubmitting, isValid },
    } = useForm<RfidTagFormValues>({
        defaultValues: initialValues,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema) as unknown as Resolver<RfidTagFormValues>,
    })

    const updateDecimalFromHex = (hexId: string, isReversed: boolean) => {
        const shouldValidate = isSubmitted

        if (hexadecimalPattern.test(hexId)) {
            const convertedHex = isReversed && hexId.length <= 20 ? reverseHex(pad(hexId, true)) : hexId
            setValue('decId', convertHexToBigInt(convertedHex), { shouldValidate })
        } else {
            setValue('decId', '', { shouldValidate })
        }
    }

    const updateHexFromDecimal = (decimalId: string, isReversed: boolean) => {
        const shouldValidate = isSubmitted

        if (decimalPattern.test(decimalId)) {
            const paddedHex = padHexId(convertBigIntToHex(decimalId))
            setValue('hexId', isReversed && paddedHex.length <= 20 ? reverseHex(paddedHex) : paddedHex, {
                shouldValidate,
            })
        } else {
            setValue('hexId', '', { shouldValidate })
        }
    }

    const save: SubmitHandler<RfidTagFormValues> = async (formValues) => {
        try {
            if (isAddingNewTag) {
                await createRfidTag({
                    nfcTag: {
                        id: formValues.hexId.toUpperCase(),
                        externalId: formValues.externalId,
                        comment: formValues.comment,
                        partnerId,
                        companyId: formValues.companyId,
                    },
                }).unwrap()
                onHide()
                toast.notifySuccess(text('msgRFIDCreated'))
            } else if (rfidTag) {
                await updateRfidTag({
                    nfcTag: {
                        ...rfidTag,
                        externalId: formValues.externalId,
                        comment: formValues.comment,
                        companyId: formValues.companyId,
                    },
                }).unwrap()
                onHide()
                toast.notifySuccess(text('msgRFIDUpdated'))
            }
        } catch (error) {
            if (!isFetchBaseQueryError(error)) return

            if (error.status === 422) {
                toast.notifyError(text('msgRFIDNotAccepted'))
            } else {
                toast.notifyError(text(isAddingNewTag ? 'msgRFIDNotCreated' : 'msgRFIDNotUpdated'))
            }
        }
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button type="button" text label={text('btnCancel')} onClick={onHide} />
            <Button
                type="submit"
                form={formId}
                label={text(isAddingNewTag ? 'btnCreate' : 'btnSave')}
                loading={isSubmitting}
                disabled={isSubmitting || (isSubmitted && !isValid)}
            />
        </div>
    )

    return (
        <Dialog
            header={text(isAddingNewTag ? 'lblNewRfidTagDialogTitle' : 'lblEditRfidTagDialogTitle')}
            visible={visible}
            onHide={onHide}
            onShow={() => reset(initialValues)}
            footer={footer}
            data-testid="rfid-tag-dialog"
        >
            <form id={formId} className="flex flex-column gap-4" onSubmit={handleSubmit(save)} noValidate>
                <Input
                    id="tag-hex-id"
                    label={text('lblHexIdField')}
                    error={errors.hexId?.message}
                    disabled={Boolean(rfidTag)}
                    required
                    {...register('hexId', {
                        onChange: (event) => updateDecimalFromHex(event.target.value, getValues('isReversed')),
                    })}
                />

                {isAddingNewTag ? (
                    <div className="flex align-items-center gap-2">
                        <Controller
                            name="isReversed"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    inputId="reverse-id-tag-checkbox"
                                    checked={field.value}
                                    onChange={(event) => {
                                        const isReversed = Boolean(event.checked)
                                        field.onChange(isReversed)
                                        updateDecimalFromHex(getValues('hexId') ?? '', isReversed)
                                    }}
                                />
                            )}
                        />
                        <label htmlFor="reverse-id-tag-checkbox" className="cursor-pointer select-none">
                            {text('lblReverseIdField')}
                        </label>
                    </div>
                ) : null}

                <Input
                    id="tag-dec-id"
                    label={text('lblDecIdField')}
                    error={errors.decId?.message}
                    disabled={Boolean(rfidTag)}
                    required
                    inputMode="numeric"
                    {...register('decId', {
                        onChange: (event) => updateHexFromDecimal(event.target.value, getValues('isReversed')),
                    })}
                />

                <Input
                    id="tag-external-id"
                    label={text('lblExternalId')}
                    error={errors.externalId?.message}
                    {...register('externalId')}
                />

                <Input
                    id="comment"
                    label={text('lblComment')}
                    error={errors.comment?.message}
                    {...register('comment')}
                />

                {(isSupportDomain && !isAddingNewTag) || isPartnerDomain ? (
                    <Controller
                        name="companyId"
                        control={control}
                        render={({ field }) => (
                            <Dropdown
                                id="rfid-tag-organisation"
                                label={text('lblOrganisationField')}
                                value={field.value}
                                showClear={Boolean(field.value)}
                                onChange={(event) => field.onChange(event.value)}
                                options={organisations.map(({ id, name }) => ({
                                    value: id ?? '',
                                    label: name ?? '',
                                }))}
                            />
                        )}
                    />
                ) : null}
            </form>
        </Dialog>
    )
})
